<template>
  <div class="space-y-6">
    <div v-if="!stats" class="flex items-center justify-center py-12">
      <div class="text-gray-600">Loading statistics...</div>
    </div>

    <template v-else>
      <div class="grid grid-cols-2 gap-6">
        <div class="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white">
          <div class="flex items-center justify-between mb-4">
            <svg class="w-8 h-8 opacity-80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <ellipse cx="12" cy="5" rx="9" ry="3" stroke="currentColor" stroke-width="2"/>
              <path d="M3 5V19A9 3 0 0 0 21 19V5" stroke="currentColor" stroke-width="2"/>
              <path d="M3 12A9 3 0 0 0 21 12" stroke="currentColor" stroke-width="2"/>
            </svg>
            <div class="text-3xl font-bold">{{ stats.total_documents }}</div>
          </div>
          <div class="text-lg font-medium">Total Documents</div>
          <div class="text-sm opacity-80 mt-1">Indexed and processed</div>
        </div>

        <div class="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
          <div class="flex items-center justify-between mb-4">
            <svg class="w-8 h-8 opacity-80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <div class="text-3xl font-bold">{{ stats.total_entities }}</div>
          </div>
          <div class="text-lg font-medium">Total Entities</div>
          <div class="text-sm opacity-80 mt-1">Extracted from documents</div>
        </div>

        <div class="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white">
          <div class="flex items-center justify-between mb-4">
            <svg class="w-8 h-8 opacity-80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 20V10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M12 20V4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M6 20V14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <div class="text-3xl font-bold">{{ stats.total_relationships }}</div>
          </div>
          <div class="text-lg font-medium">Relationships</div>
          <div class="text-sm opacity-80 mt-1">Connections in knowledge graph</div>
        </div>

        <div class="bg-gradient-to-br from-pink-500 to-pink-600 rounded-lg p-6 text-white">
          <div class="flex items-center justify-between mb-4">
            <svg class="w-8 h-8 opacity-80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M14 2V8H20" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M16 13H8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M16 17H8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M10 9H9H8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <div class="text-3xl font-bold">
              {{ Object.keys(stats.entity_types || {}).length }}
            </div>
          </div>
          <div class="text-lg font-medium">Entity Types</div>
          <div class="text-sm opacity-80 mt-1">Different categories</div>
        </div>
      </div>

      <div class="bg-white border border-gray-200 rounded-lg p-6">
        <h3 class="font-semibold text-gray-900 mb-4">Entity Types Breakdown</h3>
        <div class="space-y-3">
          <div
            v-for="([type, count], index) in Object.entries(stats.entity_types || {})"
            :key="index"
            class="flex items-center justify-between"
          >
            <div class="flex items-center space-x-3">
              <div class="w-3 h-3 bg-green-500 rounded-full"></div>
              <span class="text-gray-700 capitalize">
                {{ type.replace('_', ' ') }}
              </span>
            </div>
            <div class="flex items-center space-x-3">
              <div class="w-48 bg-gray-200 rounded-full h-2">
                <div
                  class="bg-green-500 h-2 rounded-full"
                  :style="{
                    width: `${(count / stats.total_entities) * 100}%`
                  }"
                ></div>
              </div>
              <span class="text-gray-900 font-medium w-12 text-right">
                {{ count }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="stats.indexed_files && stats.indexed_files.length > 0" class="bg-white border border-gray-200 rounded-lg p-6">
        <h3 class="font-semibold text-gray-900 mb-4">
          Indexed Files ({{ stats.indexed_files.length }})
        </h3>
        <div class="max-h-64 overflow-y-auto space-y-2">
          <div
            v-for="(file, index) in stats.indexed_files"
            :key="index"
            class="p-2 bg-gray-50 rounded text-sm text-gray-700"
          >
            {{ file }}
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'Statistics',
  
  props: {
    stats: {
      type: Object,
      default: null
    }
  }
};
</script>
